import { useEffect, useState, type CSSProperties } from "react";
import type { DeskSession } from "../hooks/useDeskSession";
import { nextOpId } from "../lib/edit-helpers";
import { CreativeOptionCard, lookPreviewStyle } from "./CreativeOptionCard.js";
import { coachLineFor } from "./tools.js";
import { ToolCartButton } from "./ToolCartButton.js";
import { useStudioStore } from "./StudioStoreContext.js";
import {
  CREATIVE_EFFECTS,
  EFFECT_LOOK_IDS,
  effectIdsAtBeat,
  effectInstanceKey,
  type EffectInstance,
} from "../lib/creative-fx.js";

/**
 * The Look tool splits into two independent sections:
 *
 *   • FILTERS  — the single color GRADE (Clean / Warm Glow / Cool Cast / Noir …).
 *     Exactly ONE is active at a time (radio behaviour) via the core `look` op.
 *     This is the reel's "look/grade" and is the only part that touches the graph.
 *
 *   • EFFECTS  — stackable atmospherics (Rain / Lightning / Focus Blur). Each
 *     toggles on/off independently and SEVERAL can be active at once (combos).
 *     Effects are game-side PRESENTATION state (see EditorScreen `activeEffects`),
 *     NOT the scored graph, so a grade + any mix of effects can co-exist.
 */
export function LookTool({
  session,
  effectInstances = [],
  currentBeatId = null,
  onToggleEffect,
  onRemoveEffect,
}: {
  session: DeskSession;
  /** Placed effects (presentation state; owned by EditorScreen). */
  effectInstances?: readonly EffectInstance[];
  /** The story beat under the playhead — where a tapped effect is placed. */
  currentBeatId?: string | null;
  /** Toggle one effect ON THE CURRENT BEAT (add if absent, remove if present). */
  onToggleEffect?: (effectId: string) => void;
  /** Remove one placed effect instance (from the placed list). */
  onRemoveEffect?: (effectId: string, beatId: string) => void;
}) {
  const { pack, packId, resolved, applyOp } = session;
  const store = useStudioStore();
  const allTreatments = pack.lookTreatments ?? [];
  const active = resolved.look;
  const selectedId = active?.treatmentId ?? null;
  // Panels show only OWNED options (plus the applied one if a loaded graph
  // carries an unowned look). Getting more is done via the cart → store. Effect
  // looks are excluded here — they render in the EFFECTS row as toggles, not as
  // single-select filter grades.
  const treatments = allTreatments.filter(
    (t) =>
      !EFFECT_LOOK_IDS.has(t.id) &&
      (store.isOptionOwned("look", t.id) || t.id === selectedId),
  );
  const selected = treatments.find((t) => t.id === selectedId) ?? null;

  // Effect placement (beat-scoped): the cards toggle on the CURRENT beat; the
  // placed list shows every effect×beat with a delete.
  const beatLabelFor = (beatId: string) =>
    pack.beats.find((b) => b.id === beatId)?.label ?? beatId;
  const effectLabelFor = (effectId: string) =>
    CREATIVE_EFFECTS.find((e) => e.id === effectId)?.label ?? effectId;
  const currentBeatLabel = currentBeatId ? beatLabelFor(currentBeatId) : null;
  const onCurrentBeat = currentBeatId ? effectIdsAtBeat(effectInstances, currentBeatId) : [];
  const beatOrder = new Map(pack.beats.map((b, i) => [b.id, i] as const));
  const placedEffects = [...effectInstances].sort((a, b) => {
    const oa = beatOrder.get(a.beatId) ?? 0;
    const ob = beatOrder.get(b.beatId) ?? 0;
    return oa !== ob ? oa - ob : a.effectId.localeCompare(b.effectId);
  });

  // Camera MOTION — folded in from the old Motion tab as a beat-scoped timed
  // effect. Unlike the stackable atmospheric effects (game-side presentation
  // state), a camera move is a core `motion` op keyed by beat (last-wins, one
  // move per beat) because the same op drives the camera-move preview. It no
  // longer feeds the score; its emotional read rides `levers.motions`. Placed on
  // the CURRENT beat (scrub, then tap) exactly like the effects above.
  const allMotions = pack.cameraMotions ?? [];
  const currentBeatMotionId = currentBeatId
    ? resolved.motions.get(currentBeatId)?.presetId ?? null
    : null;
  // Owned-only (plus the current beat's applied preset, so a loaded/unowned graph
  // keeps its move visible and clearable).
  const motionOptions = allMotions.filter(
    (m) => store.isOptionOwned("motion", m.id) || m.id === currentBeatMotionId,
  );
  const motionLabelFor = (presetId: string) =>
    allMotions.find((m) => m.id === presetId)?.label ?? presetId;
  const placedMotions = [...resolved.motions.entries()]
    .filter(([, op]) => op.presetId != null)
    .map(([beatId, op]) => ({ beatId, presetId: op.presetId as string }))
    .sort((a, b) => (beatOrder.get(a.beatId) ?? 0) - (beatOrder.get(b.beatId) ?? 0));

  const [intensity, setIntensity] = useState(
    active?.intensityMilli ?? selected?.defaultIntensityMilli ?? 1000,
  );
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [motionPreviewId, setMotionPreviewId] = useState<string | null>(null);

  // Apply a camera move on the current beat. One move per beat (last-wins), so
  // tapping the beat's active preset again clears it (radio behaviour).
  const applyMotion = (presetId: string) => {
    if (!currentBeatId) return;
    const nextPreset = presetId === currentBeatMotionId ? null : presetId;
    applyOp({
      kind: "motion",
      opId: nextOpId(nextPreset ? "motion" : "motion-clear"),
      beatId: currentBeatId,
      presetId: nextPreset,
    });
    setMotionPreviewId(null);
  };
  const clearMotionAt = (beatId: string) => {
    applyOp({
      kind: "motion",
      opId: nextOpId("motion-clear"),
      beatId,
      presetId: null,
    });
  };

  useEffect(() => {
    setIntensity(active?.intensityMilli ?? selected?.defaultIntensityMilli ?? 1000);
  }, [active?.opId, active?.intensityMilli, selected?.defaultIntensityMilli]);

  useEffect(() => {
    setPreviewId(null);
  }, [packId]);

  const applyLook = (treatmentId: string, nextIntensity?: number) => {
    const treatment = treatments.find((t) => t.id === treatmentId);
    if (!treatment) return;
    const op: {
      kind: "look";
      opId: string;
      treatmentId: string;
      intensityMilli?: number;
    } = {
      kind: "look",
      opId: nextOpId("look"),
      treatmentId,
    };
    if (treatment.intensitySupported) {
      op.intensityMilli =
        nextIntensity ??
        (treatmentId === selectedId ? intensity : treatment.defaultIntensityMilli);
    }
    applyOp(op);
    setPreviewId(null);
  };

  const previewStyle: CSSProperties | undefined = (() => {
    const id = previewId ?? selectedId;
    const t = treatments.find((x) => x.id === id);
    return t ? lookPreviewStyle(t) : undefined;
  })();

  return (
    <div className="tool-panel kid-tool" data-testid="look-tool">
      <ToolCartButton tool="look" />
      <h3>Look</h3>
      <p className="coach-line" data-testid="coach-line">
        {coachLineFor("look")}
      </p>

      {/* ── FILTERS: single color grade (radio behaviour) ─────────────────── */}
      <section className="look-section look-filters" data-testid="look-filters">
        <p className="look-section-head">
          <span className="look-section-title">Filters</span>
          <span className="look-section-note">Pick one grade</span>
        </p>
        {previewId ? (
          <p className="meta" data-testid="look-preview-hint">
            Preview only, get it in the Studio Bag to use
          </p>
        ) : null}
        <div
          className="treatment-grid kid-treatments"
          data-testid="look-cards"
          style={
            previewStyle
              ? { ["--look-preview-filter" as string]: previewStyle.filter }
              : undefined
          }
        >
          {treatments.map((t) => {
            const pressed = selectedId === t.id;
            return (
              <CreativeOptionCard
                key={t.id}
                kind="look"
                optionId={t.id}
                label={t.label}
                testId={`look-card-${t.id}`}
                className="look-card"
                selected={pressed}
                preview={
                  <span
                    className="look-thumb"
                    aria-hidden
                    style={lookPreviewStyle(t)}
                    data-look={t.id}
                  />
                }
                onApply={() => applyLook(t.id)}
                onPreview={() => setPreviewId(t.id)}
              />
            );
          })}
        </div>
      </section>

      {/* ── EFFECTS: scrub to a beat, tap to place (beat-scoped, stackable) ─── */}
      <section className="look-section look-effects" data-testid="look-effects">
        <p className="look-section-head">
          <span className="look-section-title">Effects</span>
          <span className="look-section-note">
            {currentBeatLabel ? `On: ${currentBeatLabel}` : "Scrub to a beat"}
          </span>
        </p>
        <p className="look-effects-sublabel">
          <span>Atmosphere</span>
          <span className="look-section-note">Stack any on a beat</span>
        </p>
        <div className="effect-grid" data-testid="effect-cards">
          {CREATIVE_EFFECTS.map((effect) => {
            const on = onCurrentBeat.includes(effect.id);
            return (
              <button
                key={effect.id}
                type="button"
                className={`effect-tile${on ? " is-on" : ""}`}
                data-testid={`effect-toggle-${effect.id}`}
                data-fx={effect.fx}
                data-active={on ? "1" : "0"}
                aria-pressed={on}
                aria-label={`${effect.label} on this beat${on ? ", on" : ", off"}`}
                title={`${effect.blurb} — tap to place on the current beat`}
                disabled={!currentBeatId}
                onClick={() => onToggleEffect?.(effect.id)}
              >
                <span className={`effect-icon effect-icon-${effect.fx}`} aria-hidden />
                <strong>{effect.label}</strong>
                <span className="effect-blurb">{effect.blurb}</span>
                <span className="effect-state" aria-hidden>
                  {on ? "ON" : "OFF"}
                </span>
              </button>
            );
          })}
        </div>
        {placedEffects.length > 0 && (
          <div className="effect-placed" data-testid="effect-placed">
            {placedEffects.map((inst) => (
              <div
                key={effectInstanceKey(inst)}
                className="effect-placed-row"
                data-testid={`effect-instance-${inst.effectId}-${inst.beatId}`}
              >
                <span className="effect-placed-label">{effectLabelFor(inst.effectId)}</span>
                <span className="effect-placed-beat">{beatLabelFor(inst.beatId)}</span>
                <button
                  type="button"
                  className="effect-placed-delete"
                  data-testid={`effect-delete-${inst.effectId}-${inst.beatId}`}
                  aria-label={`Remove ${effectLabelFor(inst.effectId)} from ${beatLabelFor(inst.beatId)}`}
                  onClick={() => onRemoveEffect?.(inst.effectId, inst.beatId)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── Camera move: one move per beat (folded in from the Motion tab) ── */}
        <div className="look-effects-group" data-testid="motion-group">
          <p className="look-effects-sublabel">
            <span>Camera move</span>
            <span className="look-section-note">One move per beat</span>
          </p>
          {motionPreviewId ? (
            <p className="meta" data-testid="motion-preview-hint">
              Preview only, get it in the Studio Bag to use
            </p>
          ) : null}
          <div className="treatment-grid kid-treatments" data-testid="motion-cards">
            {motionOptions.map((m) => (
              <CreativeOptionCard
                key={m.id}
                kind="motion"
                optionId={m.id}
                label={m.label}
                testId={`motion-card-${m.id}`}
                selected={currentBeatMotionId === m.id}
                onApply={() => applyMotion(m.id)}
                onPreview={() => setMotionPreviewId(m.id)}
              />
            ))}
          </div>
          {placedMotions.length > 0 && (
            <div className="effect-placed" data-testid="motion-placed">
              {placedMotions.map((inst) => (
                <div
                  key={`${inst.presetId}-${inst.beatId}`}
                  className="effect-placed-row"
                  data-testid={`motion-instance-${inst.presetId}-${inst.beatId}`}
                >
                  <span className="effect-placed-label">{motionLabelFor(inst.presetId)}</span>
                  <span className="effect-placed-beat">{beatLabelFor(inst.beatId)}</span>
                  <button
                    type="button"
                    className="effect-placed-delete"
                    data-testid={`motion-delete-${inst.presetId}-${inst.beatId}`}
                    aria-label={`Remove ${motionLabelFor(inst.presetId)} from ${beatLabelFor(inst.beatId)}`}
                    onClick={() => clearMotionAt(inst.beatId)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
